import CardTile from '@/components/cards/CardTile'
import EmptyState from '@/components/ui/EmptyState'
import { useAllCards } from '@/hooks/useCards'
import { useAllProfiles } from '@/hooks/useProfile'
import {
  buildSearchPath,
  extractPriceHint,
  matchesKeyword,
  matchesPrice,
  parseSearchParams,
} from '@/lib/search'
import { getStorePath, getStoreUrlPreview, slugifyStoreName } from '@/lib/storeSlug'
import {Search, Store, Tag} from 'lucide-react'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

function SearchPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const filters = parseSearchParams(params)
  const hint = extractPriceHint(filters.query)
  const keyword = hint.leftover
  const minPrice = filters.minPrice ?? hint.minPrice
  const maxPrice = filters.maxPrice ?? hint.maxPrice

  const [draftQuery, setDraftQuery] = useState(filters.query)
  const [draftMin, setDraftMin] = useState(filters.minPrice != null ? String(filters.minPrice) : '')
  const [draftMax, setDraftMax] = useState(filters.maxPrice != null ? String(filters.maxPrice) : '')
  const [tab, setTab] = useState<'all' | 'cards' | 'stores'>('all')

  useEffect(() => {
    setDraftQuery(filters.query)
    setDraftMin(filters.minPrice != null ? String(filters.minPrice) : '')
    setDraftMax(filters.maxPrice != null ? String(filters.maxPrice) : '')
  }, [filters.maxPrice, filters.minPrice, filters.query])

  const { cards, loading: cardsLoading } = useAllCards()
  const { profiles, byUserId, loading: storesLoading } = useAllProfiles()
  const loading = cardsLoading || storesLoading

  const cardResults = useMemo(() => {
    return cards.filter(card => {
      const textMatch = matchesKeyword(
        [card.title, card.description, card.productType, card.condition, card.listingType],
        keyword,
      )
      return textMatch && matchesPrice(card.price, minPrice, maxPrice)
    })
  }, [cards, keyword, maxPrice, minPrice])

  const storeResults = useMemo(() => {
    const storeQuery = keyword || filters.query
    if (!storeQuery) return []
    return profiles.filter(profile =>
      matchesKeyword(
        [profile.storeName, profile.storeSlug, profile.bio, slugifyStoreName(profile.storeName)],
        storeQuery,
      ),
    )
  }, [filters.query, keyword, profiles])

  const hasQuery = Boolean(filters.query || minPrice != null || maxPrice != null)
  const showCards = tab !== 'stores'
  const showStores = tab !== 'cards'

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    navigate(buildSearchPath({
      query: draftQuery,
      minPrice: draftMin ? Number(draftMin) : undefined,
      maxPrice: draftMax ? Number(draftMax) : undefined,
    }))
  }

  return (
    <div className="mx-auto max-w-6xl px-4 pb-14 pt-6 sm:px-6 sm:pt-8">
      <div className="mb-6">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border-2 border-white/20 bg-white/10 px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-white">
          <Search className="h-3.5 w-3.5" />
          Search My Card Shop
        </div>
        <h1 className="font-display text-3xl font-extrabold text-white sm:text-5xl">Find cards and stores</h1>
        <p className="mt-2 max-w-xl text-sm font-semibold text-white/55">
          Search Pokemon cards by name, keyword, or price, and look up shops by store name.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mb-6 rounded-[28px] border-4 border-black bg-white/[0.05] p-4 shadow-[8px_8px_0_rgba(59,76,202,0.28)]">
        <label className="mb-2 block text-xs font-extrabold uppercase tracking-wide text-white/45" htmlFor="search-query">
          Name or keyword
        </label>
        <input
          id="search-query"
          value={draftQuery}
          onChange={event => setDraftQuery(event.target.value)}
          placeholder="Charizard, ETB, King Brickachu..."
          className="mb-3 w-full rounded-2xl border-2 border-white/15 bg-black/50 px-4 py-3 text-sm font-semibold text-white outline-none placeholder:text-white/35 focus:border-[#FFDE00]"
        />
        <div className="mb-4 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-2 block text-xs font-extrabold uppercase tracking-wide text-white/45" htmlFor="search-min">
              Min price
            </label>
            <input
              id="search-min"
              type="number"
              min="0"
              step="0.01"
              value={draftMin}
              onChange={event => setDraftMin(event.target.value)}
              placeholder="0"
              className="w-full rounded-2xl border-2 border-white/15 bg-black/50 px-4 py-3 text-sm font-semibold text-white outline-none placeholder:text-white/35 focus:border-[#FFDE00]"
            />
          </div>
          <div>
            <label className="mb-2 block text-xs font-extrabold uppercase tracking-wide text-white/45" htmlFor="search-max">
              Max price
            </label>
            <input
              id="search-max"
              type="number"
              min="0"
              step="0.01"
              value={draftMax}
              onChange={event => setDraftMax(event.target.value)}
              placeholder="100"
              className="w-full rounded-2xl border-2 border-white/15 bg-black/50 px-4 py-3 text-sm font-semibold text-white outline-none placeholder:text-white/35 focus:border-[#FFDE00]"
            />
          </div>
        </div>
        <button
          type="submit"
          className="inline-flex items-center gap-2 rounded-full bg-[#FFDE00] px-5 py-2.5 text-sm font-extrabold text-black shadow-[0_4px_0_#c9a800] transition-transform hover:-translate-y-0.5"
        >
          <Search className="h-4 w-4" />
          Search
        </button>
      </form>

      <div className="mb-6 flex flex-wrap gap-2">
        {[
          { value: 'all', label: `All (${cardResults.length + storeResults.length})` },
          { value: 'cards', label: `Cards (${cardResults.length})` },
          { value: 'stores', label: `Stores (${storeResults.length})` },
        ].map(item => {
          const active = tab === item.value
          return (
            <button
              key={item.value}
              type="button"
              onClick={() => setTab(item.value as typeof tab)}
              className={`rounded-full border-2 px-3.5 py-1.5 text-xs font-extrabold uppercase tracking-wide transition-transform hover:-translate-y-0.5 ${
                active
                  ? 'border-[#FFDE00] bg-[#FFDE00] text-black shadow-[0_3px_0_#c9a800]'
                  : 'border-white/15 bg-white/5 text-white/70'
              }`}
            >
              {item.label}
            </button>
          )
        })}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] animate-pulse rounded-2xl bg-white/5" />
          ))}
        </div>
      ) : !hasQuery ? (
        <EmptyState
          icon={Search}
          title="Start a search"
          description="Try a card name, a keyword like ETB, a price like under 25, or a store name."
        />
      ) : cardResults.length === 0 && storeResults.length === 0 ? (
        <EmptyState
          icon={Search}
          title="No matches"
          description="Nothing matched that name, keyword, price, or store. Try a shorter search."
        />
      ) : (
        <div className="space-y-10">
          {showStores && storeResults.length > 0 ? (
            <section>
              <div className="mb-4 flex items-center gap-2">
                <Store className="h-4 w-4 text-[#FFDE00]" />
                <h2 className="font-display text-2xl font-extrabold text-white">Stores</h2>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {storeResults.map(profile => (
                  <Link
                    key={profile._id}
                    to={getStorePath(profile)}
                    className="flex items-center gap-3 rounded-[24px] border-2 border-white/10 bg-white/[0.04] p-3 transition-transform hover:-translate-y-0.5 hover:border-[#FFDE00]/50"
                  >
                    {profile.avatarUrl ? (
                      <img src={profile.avatarUrl} alt="" className="h-14 w-14 rounded-full object-cover" />
                    ) : (
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#FF1A1A] font-extrabold text-white">
                        {(profile.storeName || '?')[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="truncate font-display text-lg font-extrabold text-white">{profile.storeName}</p>
                      <p className="truncate text-xs font-extrabold text-[#8EA2FF]">
                        {getStoreUrlPreview(profile.storeName, profile.storeSlug)}
                      </p>
                      {profile.bio ? <p className="mt-1 line-clamp-2 text-xs font-semibold text-white/50">{profile.bio}</p> : null}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ) : null}

          {showCards && cardResults.length > 0 ? (
            <section>
              <div className="mb-4 flex items-center gap-2">
                <Tag className="h-4 w-4 text-[#FFDE00]" />
                <h2 className="font-display text-2xl font-extrabold text-white">Cards</h2>
              </div>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {cardResults.map(card => {
                  const profile = byUserId.get(card.userId)
                  return (
                    <CardTile
                      key={card._id}
                      card={card}
                      storeName={profile?.storeName}
                      storeSlug={profile?.storeSlug || slugifyStoreName(profile?.storeName)}
                      storeUserId={card.userId}
                    />
                  )
                })}
              </div>
            </section>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default SearchPage
